import {useEffect, useState, useCallback} from 'react';
import {
  PageHeader,
  SaveCancelFooter,
  Alert,
  ConfirmationModal
} from '@/components/molecules';
import {Breadcrumb} from '@/components/atoms';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {CreateTierContainer} from '@/containers/ClubPlus/CreateTier/CreateTierContainer';
import {FormProvider, useForm} from 'react-hook-form';
import {
  getCreateTierSchema,
  getDefaultValues
} from '@/pages/ClubPlusPages/CreateTier/CreateTierSchemas';
import {useAppDispatch, useAppSelector} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {useIntl} from 'react-intl';
import {createTier, getTierDetails} from '@/services/ClubPlus/TierService';
import {detectFormChanges} from '@/utils/formUtils';
import './_tierDetails.scss';
import {zodResolver} from '@hookform/resolvers/zod';
import {transformTierDataToForm} from '@/services/ClubPlus/tierFormTransform';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import enUS from '@/internationalization/i18n/en-us';
import {logger} from '@/helpers/logger';

export const TierDetails = () => {
  const {formatMessage} = useIntl();
  const {id: tierId} = useParams();
  const [searchParams] = useSearchParams();
  const VALID_STATUSES = ['draft', 'published', 'in progress'];
  const rawStatus = searchParams.get('status');
  const status = VALID_STATUSES.includes(rawStatus?.toLowerCase())
    ? rawStatus
    : null;

  const countryCode = useAppSelector(selectCountryCode);
  const countryName = enUS.COUNTRIES[countryCode];

  const tierSchema = getCreateTierSchema(countryCode);
  const tierDefaultValues = getDefaultValues(countryCode);

  const methods = useForm({
    defaultValues: tierDefaultValues,
    resolver: zodResolver(tierSchema),
    mode: 'onChange'
  });

  const [cancelModal, setCancelModal] = useState(false);
  const [draftModal, setDraftModal] = useState(false);
  const [draftExistsModal, setDraftExistsModal] = useState(false);
  const [phase, setPhase] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [formIsValid, setFormIsValid] = useState({
    phase1: false,
    phase2: false
  });
  const [canSaveDraft, setCanSaveDraft] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tierLastUpdated, setTierLastUpdated] = useState(null);
  const [tierName, setTierName] = useState('');
  const [targetPhase, setTargetPhase] = useState(1);
  const [draftData, setDraftData] = useState(null);
  const [publishedData, setPublishedData] = useState(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const loadTierDetails = async () => {
      try {
        setIsLoadingData(true);
        const queryParams = status ? `status=${status}` : '';
        const tierData = await getTierDetails(tierId, queryParams);

        setTierLastUpdated(tierData.lastUpdated);
        setPublishedData(tierData);
        setTierName(tierData.name);

        const formData = transformTierDataToForm(
          tierData,
          formatMessage,
          countryCode
        );
        methods.reset(formData);
      } catch (error) {
        logger.error('Error loading tier details:', error);
        setApiError(formatMessage({id: 'CLUB_PLUS.TIER_LOAD_ERROR_MESSAGE'}));
      } finally {
        setIsLoadingData(false);
      }
    };

    if (tierId) {
      loadTierDetails();
    }
  }, [tierId, status, methods, formatMessage, countryCode]);

  useEffect(() => {
    const subscription = methods.watch(() => {
      const activities = methods.getValues('activities') || [];
      const hasValidActivities =
        activities.length > 0 &&
        activities.every(
          (activity) =>
            activity.activityID && activity.activityName && activity.multiplier
        );

      const hasAtLeastOneValidActivity =
        activities.length > 0 &&
        activities.some(
          (activity) =>
            activity.activityID && activity.activityName && activity.multiplier
        );

      const tierName = methods.getValues('name');

      const currentTierId = methods.getValues('tierId');
      if (currentTierId && typeof currentTierId === 'number') {
        methods.setValue('tierId', String(currentTierId), {shouldDirty: false});
      }

      activities.forEach((activity, index) => {
        if (activity.activityID && typeof activity.activityID === 'number') {
          methods.setValue(
            `activities.${index}.activityID`,
            String(activity.activityID),
            {shouldDirty: false}
          );
        }
      });

      setFormIsValid((prev) => ({
        ...prev,
        phase1: tierName && hasValidActivities
      }));

      const tierId = methods.getValues('tierId');
      const isDirty = methods.formState.isDirty;

      let hasChanges = isDirty;
      if (isEditMode && originalFormData) {
        const currentFormData = methods.getValues();
        hasChanges = detectFormChanges(
          currentFormData,
          originalFormData,
          isDirty
        );
      }

      setCanSaveDraft(tierId && hasAtLeastOneValidActivity && hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [methods, isEditMode, phase, originalFormData]);

  const getFormError = useCallback(() => {
    if (phase === 1) {
      return !formIsValid.phase1;
    } else if (phase === 2) {
      const formState = methods.formState;
      const isValid = formState.isValid;
      const hasErrors = Object.keys(formState.errors || {}).length > 0;

      if (!isValid && !hasErrors && !formState.isValidating) {
        const formValues = methods.getValues();
        const contentLangs = Object.keys(formValues.content || {});
        let hasValidationIssues = false;

        if (!formValues.tierId || !formValues.activities?.length) {
          hasValidationIssues = true;
        }

        contentLangs.forEach((lang) => {
          const content = formValues.content[lang];
          if (
            !content?.tierName ||
            !content?.imageTierIcon ||
            !content?.bannerHeader ||
            !content?.bannerImage ||
            !content?.detailsDescription ||
            !content?.detailsImage
          ) {
            hasValidationIssues = true;
          }
        });

        if (!hasValidationIssues) {
          return false;
        }
      }

      return !isValid;
    }

    return false;
  }, [formIsValid, methods, phase]);

  useEffect(
    () => async () => {
      const hasError = getFormError();
      if (hasError) {
        await methods.trigger();
      }
    },
    [methods, phase, getFormError]
  );

  const handleCancelYes = () => {
    setCancelModal(false);
    setApiError(null);
    if (isEditMode) {
      setIsEditMode(false);
      setPhase(3);
    } else {
      navigate(ROUTES.CLUB_HOME);
    }
  };

  const isValidActivity = (activity) =>
    activity.activityID &&
    activity.activityID.trim() !== '' &&
    activity.multiplier &&
    parseFloat(activity.multiplier) > 0;

  const formatTierData = (formData) => {
    const transformedContent = {};

    Object.keys(formData.content || {}).forEach((langKey) => {
      const content = formData.content[langKey];
      const {detailsTermsAndConditions, detailsDescription, ...restContent} =
        content;
      transformedContent[langKey] = {
        ...restContent,
        descriptionDetail: detailsDescription,
        termsAndConditions: detailsTermsAndConditions
      };
    });

    return {
      tierId: formData.tierId || '',
      activities: formData.activities
        .filter(isValidActivity)
        .map((activity) => ({
          id: activity.activityID,
          multiplier: parseFloat(activity.multiplier) || 0
        })),
      content: transformedContent
    };
  };

  useEffect(() => {
    if (phase === 3 && publishedData && !isEditMode) {
      const formData = transformTierDataToForm(
        publishedData,
        formatMessage,
        countryCode
      );
      methods.reset(formData);
    }
  }, [phase, publishedData, methods, formatMessage, isEditMode, countryCode]);

  const handleTierAction = async (isDraft = false) => {
    try {
      setIsLoading(true);
      setApiError(null);
      const formData = methods.getValues();
      const tierData = formatTierData(formData);

      await createTier(tierData, isDraft);

      const cleanedActivities = (formData.activities || []).filter(
        isValidActivity
      );

      methods.setValue('activities', cleanedActivities, {shouldDirty: false});

      const updatedFormData = {
        ...formData,
        activities: cleanedActivities
      };

      if (isDraft) {
        setDraftData(updatedFormData);
      } else {
        setPublishedData(updatedFormData);
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: formatMessage({
            id: isDraft
              ? 'CLUB_PLUS.TIER_UPDATE_DRAFT_SUCCESS'
              : 'CLUB_PLUS.TIER_UPDATE_SUCCESS_MESSAGE'
          }),
          type: 'success'
        })
      );

      setIsEditMode(false);
      setPhase(3);
    } catch (error) {
      logger.error(
        `Error ${isDraft ? 'saving tier as draft' : 'updating tier'}:`,
        error
      );

      const errorMessage = formatMessage({
        id: 'CLUB_PLUS.TIER_UPDATE_ERROR_MESSAGE'
      });

      setApiError(errorMessage);

      dispatch(
        setToastNotifications({
          show: true,
          message: errorMessage,
          type: 'error'
        })
      );
    } finally {
      setIsLoading(false);

      if (isDraft) {
        setDraftModal(false);
      }
    }
  };

  const checkForDraftAndEdit = async (phaseToNavigate = 1) => {
    try {
      setTargetPhase(phaseToNavigate);

      const draftQueryParams = 'status=DRAFT';
      const draftTierData = await getTierDetails(tierId, draftQueryParams);

      setDraftData(draftTierData);
      setDraftExistsModal(true);
    } catch {
      startEditMode(publishedData, phaseToNavigate);
    }
  };

  const startEditMode = (tierData, phaseToNavigate = 1) => {
    if (!tierData) {
      return;
    }

    const formData = transformTierDataToForm(
      tierData,
      formatMessage,
      countryCode
    );

    methods.reset(formData, {
      keepDefaultValues: false,
      keepValues: false,
      keepErrors: false,
      keepDirty: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false
    });

    setOriginalFormData(JSON.parse(JSON.stringify(formData)));

    setIsEditMode(true);
    setPhase(phaseToNavigate);

    setTimeout(() => {
      methods.trigger().then(() => {
        if (
          !methods.formState.isValid &&
          Object.keys(methods.formState.errors || {}).length === 0
        ) {
          setTimeout(() => {
            methods.trigger();
          }, 50);
        }
      });
    }, 150);
  };

  const handleUseDraft = () => {
    setDraftExistsModal(false);
    startEditMode(draftData, targetPhase);
  };

  const handleUsePublished = () => {
    setDraftExistsModal(false);
    startEditMode(publishedData, targetPhase);
  };

  const handleEdit = (phaseToNavigate = 1) => {
    checkForDraftAndEdit(phaseToNavigate);
  };

  const handleSetPhase = (newPhase) => {
    if (newPhase === 2) {
      const currentTierId = methods.getValues('tierId');
      const activities = methods.getValues('activities') || [];

      if (currentTierId && typeof currentTierId === 'number') {
        methods.setValue('tierId', String(currentTierId));
      }

      activities.forEach((activity, index) => {
        if (activity.activityID && typeof activity.activityID === 'number') {
          methods.setValue(
            `activities.${index}.activityID`,
            String(activity.activityID)
          );
        }
      });

      setTimeout(() => {
        methods.trigger();
      }, 50);
    }

    setPhase(newPhase);
  };

  if (isLoadingData) {
    return <div>Loading tier details...</div>;
  }

  return (
    <>
      {apiError && (
        <Alert
          type='error'
          message={apiError}
          show={true}
          onCloseClick={() => setApiError(null)}
        />
      )}
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: ROUTES.HOME,
              label: ''
            },
            {
              href: ROUTES.CLUB_HOME,
              label: 'Club+'
            },
            {
              href: ROUTES.TIER_DETAILS.replace(':id', tierId),
              label: `Create Tier${countryName ? ` ${countryName}` : ''}`
            }
          ]
        }}
        id={'tier_details_breadcrumb'}
      />
      <PageHeader title={tierName} />
      <FormProvider {...methods}>
        <CreateTierContainer
          phase={isEditMode ? phase : 3}
          setPhase={handleSetPhase}
          isEditMode={isEditMode}
          onEdit={handleEdit}
          lastUpdated={tierLastUpdated}
          isEditingExistingTier={true}
          tierData={publishedData}
        />
        {isEditMode && (
          <SaveCancelFooter
            handleCancel={() => {
              setApiError(null);
              setCancelModal(true);
            }}
            nextAction={() => {
              if (phase === 3) {
                handleTierAction(false);
              } else {
                setPhase((currentPhase) => currentPhase + 1);
              }
            }}
            draftAction={() => {
              setApiError(null);
              setDraftModal(true);
            }}
            isReview={phase === 3}
            FormError={getFormError() || isLoading}
            disableDraft={!canSaveDraft}
            isLoading={isLoading}
          />
        )}
      </FormProvider>
      <ConfirmationModal
        showModal={cancelModal}
        setShowModal={setCancelModal}
        handleConfirm={handleCancelYes}
        title={formatMessage({id: 'CLUB_PLUS.TIER_CANCEL_MODAL_TITLE'})}
        text={formatMessage({id: 'CLUB_PLUS.TIER_CANCEL_MODAL_TEXT'})}
        primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
        secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
        type='S'
        className='tier-details-modal club-plus-confirm-modal'
      />
      <ConfirmationModal
        showModal={draftModal}
        setShowModal={setDraftModal}
        handleConfirm={() => handleTierAction(true)}
        title={formatMessage({id: 'CLUB_PLUS.TIER_UPDATE_DRAFT_TITLE'})}
        text={formatMessage({id: 'CLUB_PLUS.TIER_UPDATE_DRAFT_TEXT'})}
        primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
        secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
        type='S'
        className='tier-details-modal club-plus-confirm-modal'
      />
      <ConfirmationModal
        showModal={draftExistsModal}
        setShowModal={setDraftExistsModal}
        handleConfirm={handleUseDraft}
        title='Draft Version Found'
        text='A draft version of this tier exists. Do you want to continue editing from the draft or start fresh from the published version?'
        primaryButtonText='Use Draft'
        secondaryButtonText='Use Published'
        type='S'
        className='tier-details-modal club-plus-confirm-modal'
        secondaryButtonProps={{
          onClick: handleUsePublished,
          size: 'medium',
          text: 'Use Published',
          variant: 'secondary'
        }}
      />
    </>
  );
};
